import {
  Card,
  CardResponse,
  Language,
  Player,
  View,
  ViewResponse,
} from '@yleisradio/areena-types';
import {
  AudioItem,
  CuePointItem,
  GeoLocation,
  MediaItem,
  ServiceScheduleItem,
  StreamComponents,
  SubtitleItem,
} from '../../appTypings';
import { formatLabel, getRawLabel } from '../../utils/labels';
import { getTranslation } from '../../localizations/translations';
import { isBroadcastCard } from '../../View/card/presentation';
import { isServiceId } from '../../utils/item';
import { getIsGeoBlocked, getRegion } from '../../utils/geoBlock';
import { getTwoDigitMinutes } from '../../utils/dateUtils';

export const getTitles = (
  { title: viewTitle }: View,
  program: Card | undefined
) => {
  if (program && isBroadcastCard(program?.presentation)) {
    const episodeTitle = formatLabel(program, 'episodeTitle');
    return {
      seriesTitle: program.title && episodeTitle ? program.title : undefined,
      episodeTitle: episodeTitle || program.title,
    };
  }
  return {
    seriesTitle: program?.title ? viewTitle : undefined,
    episodeTitle: program?.title ? program.title : viewTitle,
  };
};

export const getImage = (view: View, episode: Card | undefined) =>
  episode?.image?.id ? episode.image : view.image;

export const isFakeLiveVod = (schedule: ServiceScheduleItem[] | undefined) =>
  schedule?.length === 1;

export const getLiveStreamComponents = (
  serviceSchedule: ServiceScheduleItem[],
  time: Date
): StreamComponents => {
  const currentProgram = isFakeLiveVod(serviceSchedule)
    ? serviceSchedule[0]
    : serviceSchedule.find(item => new Date(item.endTime) > time);

  const audioItems: AudioItem[] | undefined = currentProgram?.audioTracks.map(
    item => {
      return {
        language: item.language,
        kind: item.type,
        index: item.trackIndex,
      };
    }
  );

  const subtitleItems: SubtitleItem[] | undefined =
    currentProgram?.subtitles.map(item => {
      return {
        language: item.language,
        type: item.type,
        kind: item.kind,
        index: item.trackIndex,
      };
    });

  return {
    subtitles: subtitleItems ?? [],
    audioTracks: audioItems ?? [],
  };
};

export const getStreamComponents = (
  media: MediaItem,
  playFromInstant?: string
): StreamComponents => {
  if (media.serviceSchedule) {
    const time = playFromInstant ? new Date(playFromInstant) : new Date();
    return getLiveStreamComponents(media.serviceSchedule, time);
  } else {
    return {
      subtitles: media.subtitles || [],
      audioTracks: media.audioTracks || [],
    };
  }
};

export const isPositionOnCuePoint = (
  position: number,
  cuePoint: CuePointItem | undefined
) => cuePoint && position > cuePoint.start && position < cuePoint.end;

export const getPlayerViewId = (itemId: string, publicationId?: string) =>
  itemId + (typeof publicationId === 'string' ? `-${publicationId}` : '');

export const getSimulcastTitle = (id: string): string => {
  switch (id) {
    case 'yle-tv1':
      return 'Yle TV1';
    case 'yle-tv2':
      return 'Yle TV2';
    case 'yle-teema-fem':
      return 'Yle Teema Fem';
    default:
      return '';
  }
};

export const getSecondsToSpeech = (time: number, languageCode: Language) => {
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const seconds = Math.floor(time % 60);

  const hoursText =
    hours > 0
      ? `${hours} ${getTranslation('player_ariaAnnouncements_hours', languageCode)}`
      : '';
  const minutesText =
    minutes > 0
      ? `${minutes} ${getTranslation('player_ariaAnnouncements_minutes', languageCode)}`
      : '';
  const secondsText =
    seconds > 0
      ? `${seconds} ${getTranslation('player_ariaAnnouncements_seconds', languageCode)}`
      : '';

  const parts = [hoursText, minutesText, secondsText].filter(Boolean);

  return parts.join(' ');
};

export const secondsToTime = (secs: number) => {
  if (isNaN(secs)) {
    return '00:00:00';
  }
  return (
    ('0' + Math.floor(secs / (60 * 60))).slice(-2) +
    ':' +
    ('0' + Math.floor((secs / 60) % 60)).slice(-2) +
    ':' +
    ('0' + Math.floor(secs % 60)).slice(-2)
  );
};

export const parseTimestamp = (date: Date) => {
  if (isNaN(+date)) {
    return '00:00';
  }
  return (
    ('0' + date.getHours()).slice(-2) +
    ':' +
    ('0' + date.getMinutes()).slice(-2)
  );
};

export const getLiveDuration = () => {
  const currentTime = new Date();
  return parseTimestamp(currentTime);
};

export const getLivePosition = (seconds: number) => {
  const currentTime = Date.now();
  const dvrWindowTime = new Date(currentTime - seconds * 1000);
  return parseTimestamp(dvrWindowTime);
};

const dateIsBetween = (
  checkDateTime: Date,
  startDateTime: Date,
  endDateTime: Date
) => {
  return checkDateTime >= startDateTime && checkDateTime <= endDateTime;
};

export const getCurrentSimulcastProgram = (
  time: number,
  data: CardResponse | undefined
) => {
  if (!data?.data) return undefined;

  const dates = data?.data?.map((card, index) => {
    const endDate = card.labels?.find(
      label => label.type === 'broadcastEndDate'
    );
    const startDate = card.labels?.find(
      label => label.type === 'broadcastStartDate'
    );
    return {
      endDate,
      startDate,
      index,
    };
  });

  const seekTime = new Date(Date.now() - time * 1000);

  let currentProgramDateEntry = dates?.find(program => {
    if (!program.startDate?.raw || !program.endDate?.raw) return false;

    const start = new Date(program.startDate.raw);
    const end = new Date(program.endDate.raw);
    return dateIsBetween(seekTime, start, end);
  });

  // Handle gaps in schedule data
  if (!currentProgramDateEntry) {
    let seekTimeBack = seekTime.getTime();
    while (!currentProgramDateEntry) {
      seekTimeBack = seekTimeBack - 15000;
      currentProgramDateEntry = dates?.find(program => {
        if (!program.startDate?.raw || !program.endDate?.raw) return false;

        const start = new Date(program.startDate.raw);
        const end = new Date(program.endDate.raw);
        return dateIsBetween(new Date(seekTimeBack), start, end);
      });
      if (seekTimeBack < seekTime.getTime() - 600000) {
        currentProgramDateEntry = dates?.[0];
      }
    }
  }

  return data?.data?.[currentProgramDateEntry.index];
};

export const getIdToRequestMediaWith = (
  playerData: Player | undefined,
  itemId: string
) => {
  if (!playerData) {
    return undefined;
  }
  const itemIdToPlay = playerData?.item.id ?? itemId;
  return isServiceId(itemIdToPlay) ? itemIdToPlay : playerData?.media.id;
};

export const getNoMediaView = (
  playerView: ViewResponse | undefined,
  geoLocation: GeoLocation
) => {
  const playerViewData = playerView?.data;
  if (!playerViewData) {
    return {
      message: (languageCode: Language) =>
        getTranslation(
          'playerErrorMessages_somethingWentWrongTryAgain',
          languageCode
        ),
    };
  }
  const rawRefreshAt = playerView.meta?.refreshAt;

  const broadcastStartDateRaw = playerViewData.header?.labels?.find(label =>
    label.type.includes('broadcastStartDate')
  )?.raw;

  if (broadcastStartDateRaw) {
    const oneHourInMs = 60 * 60 * 1000;
    const broadcastStartDate = new Date(broadcastStartDateRaw);

    const isBroadcastStartDateInTheFuture = broadcastStartDate > new Date();

    if (isBroadcastStartDateInTheFuture) {
      const timeLeft = broadcastStartDate.getTime() - Date.now();
      const isStartingSoon = timeLeft < oneHourInMs;

      return {
        message: (languageCode: Language) =>
          isStartingSoon
            ? getTranslation('playerErrorMessages_startsSoon', languageCode)
            : `${getTranslation(
                'playerErrorMessages_upcoming',
                languageCode
              )} ${broadcastStartDate.getHours()}:${getTwoDigitMinutes(
                broadcastStartDate
              )}`,
        timeLeft: isStartingSoon ? timeLeft : undefined,
        refreshAt: rawRefreshAt,
      };
    }
  }

  const ondemandStatus =
    playerViewData.header &&
    getRawLabel(playerViewData.header, 'ondemandStatus');

  if (ondemandStatus === 'expired') {
    return {
      message: (languageCode: Language) =>
        getTranslation('playerErrorMessages_expired', languageCode),
    };
  }

  const isGeoBlocked = getIsGeoBlocked(
    geoLocation,
    getRegion(playerViewData.header?.labels || [])
  );

  if (isGeoBlocked) {
    return {
      message: (languageCode: Language) =>
        getTranslation('playerErrorMessages_geoblocked', languageCode),
    };
  }

  const noMediaInPlayerViewData =
    playerViewData && !playerViewData.header?.player?.media.id;
  if (noMediaInPlayerViewData) {
    return {
      message: (languageCode: Language) =>
        getTranslation(
          'playerErrorMessages_somethingWentWrongTryAgain',
          languageCode
        ),
    };
  }
};
